import * as React from "react";
import { useFloating, autoUpdate, offset, flip, shift, useClick, useDismiss, useRole, useInteractions, useTransitionStatus, FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import clsx from "clsx";
import { Badge } from "../../badge/badge.mjs";
import { Button } from "../../button/button.mjs";
import { Card } from "../../card/card/card.mjs";
import { CardContent } from "../../card/card-content/card-content.mjs";
import { ChoiceGroup } from "../../form/choice-group/choice-group.mjs";
import { Icon } from "../../icon/icon/icon.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { Skeleton } from "../../skeleton/skeleton/skeleton.mjs";
import { SkeletonItem } from "../../skeleton/skeleton-item/skeleton-item.mjs";
import { Slider } from "../../form/slider/slider.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
const HorizontalFilterItemSwitch = (props) => {
  var _a, _b;
  const switchButtonRef = React.useRef(null);
  const labels = useLabels();
  const initialIsOpen = !!((_b = (_a = props.inputType) == null ? void 0 : _a.switch) == null ? void 0 : _b.defaultValue) || !!props.isOpen;
  const [internalIsOpen, setInternalIsOpen] = React.useState(initialIsOpen);
  const isControlled = props.onToggle && typeof props.isOpen !== "undefined";
  const isOpen = isControlled ? props.isOpen : internalIsOpen;
  const showClearButton = isOpen;
  const handleSwitchClick = (event) => {
    event.preventDefault();
    if (!isControlled) {
      setInternalIsOpen(!isOpen);
    }
    if (props.onClose && !isOpen) {
      props.onClose();
    }
    if (props.onToggle) {
      props.onToggle(!isOpen);
    }
  };
  const onClearButtonClick = (event) => {
    var _a2;
    event.preventDefault();
    if (!isControlled) {
      setInternalIsOpen(false);
    }
    if (props.onClose) {
      props.onClose();
    }
    if (props.onToggle) {
      props.onToggle(false);
    }
    (_a2 = switchButtonRef.current) == null ? void 0 : _a2.focus();
  };
  return /* @__PURE__ */ React.createElement("div", { className: "horizontal-filter-item__wrapper" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      ref: switchButtonRef,
      className: clsx("horizontal-filter-item", {
        "is-active": isOpen,
        "is-clearable": showClearButton
      }),
      onClick: handleSwitchClick,
      type: "button",
      id: props.id,
      "aria-pressed": isOpen
    },
    props.label
  ), showClearButton && /* @__PURE__ */ React.createElement(
    Button,
    {
      id: "".concat(props.id, "-close"),
      onClick: onClearButtonClick,
      icon: "#close-round",
      link: true,
      text: labels.get("clear"),
      className: "horizontal-filter-item__clear-btn",
      "aria-labelledby": "".concat(props.id, "-close ").concat(props.id)
    }
  ));
};
const HorizontalFilterItemChoiceGroupOrSlider = (props) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
  const choiceGroupValueProp = typeof ((_b = (_a = props.inputType) == null ? void 0 : _a.choiceGroup) == null ? void 0 : _b.value) === "string" ? [(_d = (_c = props.inputType) == null ? void 0 : _c.choiceGroup) == null ? void 0 : _d.value] : (_f = (_e = props.inputType) == null ? void 0 : _e.choiceGroup) == null ? void 0 : _f.value;
  const choiceGroupDefaultValue = typeof ((_h = (_g = props.inputType) == null ? void 0 : _g.choiceGroup) == null ? void 0 : _h.defaultValue) === "string" ? [(_j = (_i = props.inputType) == null ? void 0 : _i.choiceGroup) == null ? void 0 : _j.defaultValue] : (_l = (_k = props.inputType) == null ? void 0 : _k.choiceGroup) == null ? void 0 : _l.defaultValue;
  const choiceGroupValue = choiceGroupValueProp || choiceGroupDefaultValue;
  const defaultIsActiveValue = ((_n = (_m = props.inputType) == null ? void 0 : _m.slider) == null ? void 0 : _n.defaultValue) || !!(choiceGroupValue == null ? void 0 : choiceGroupValue.length);
  const defaultSliderValue = !!((_p = (_o = props.inputType) == null ? void 0 : _o.slider) == null ? void 0 : _p.defaultValue);
  const labels = useLabels();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isActive, setIsActive] = React.useState(defaultIsActiveValue);
  const [showSliderValue, setShowSliderValue] = React.useState(defaultSliderValue);
  const { portalRoot } = useTheme();
  const showClearButton = ((_q = props.inputType) == null ? void 0 : _q.choiceGroup) ? !!(choiceGroupValue == null ? void 0 : choiceGroupValue.length) : showSliderValue;
  const isControlled = !!props.onToggle && typeof props.isOpen !== "undefined";
  const isOpen = isControlled ? props.isOpen : isModalOpen;
  React.useEffect(() => {
    var _a2;
    if (isOpen) {
      setIsActive(true);
    } else {
      const isInputActive = ((_a2 = props.inputType) == null ? void 0 : _a2.choiceGroup) ? !!(choiceGroupValue == null ? void 0 : choiceGroupValue.length) : showSliderValue;
      setIsActive(isInputActive);
    }
  }, [choiceGroupValue == null ? void 0 : choiceGroupValue.length, isOpen, showSliderValue, (_r = props.inputType) == null ? void 0 : _r.choiceGroup]);
  const handleModalOpenChange = (open) => {
    setIsModalOpen(open);
    if (props.onClose && isOpen && !open) {
      props.onClose();
    }
    if (props.onToggle) {
      props.onToggle(open);
    }
  };
  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: handleModalOpenChange,
    middleware: [offset(), flip({ fallbackAxisSideDirection: "end" }), shift()],
    whileElementsMounted: autoUpdate
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const { isMounted, status } = useTransitionStatus(context, { duration: 100 });
  const getSliderValues = (slider) => {
    var _a2, _b2;
    const values = (_b2 = (_a2 = props.inputType) == null ? void 0 : _a2.slider) == null ? void 0 : _b2.start;
    if (values) {
      if (values[0] === values[1]) {
        return ": ".concat(values[0]).concat(slider.unit);
      }
      return ": ".concat(values[0], "-").concat(values[1]).concat(slider.unit);
    }
  };
  const onClearButtonClick = (event) => {
    var _a2;
    event.preventDefault();
    props.clearButtonOnClick && props.clearButtonOnClick(event);
    handleModalOpenChange(false);
    setShowSliderValue(false);
    const buttonElement = refs.domReference;
    (_a2 = buttonElement.current) == null ? void 0 : _a2.focus();
  };
  const getZIndex = () => {
    const buttonRef = refs.domReference;
    if (buttonRef.current) {
      const inModal = buttonRef.current.closest(".modal") !== null;
      if (inModal) {
        return 30;
      }
    }
    return 19;
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "horizontal-filter-item__wrapper" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      className: clsx("horizontal-filter-item", {
        ["is-open"]: isOpen,
        ["is-active"]: isActive,
        ["is-clearable"]: showClearButton
      }),
      ref: refs.setReference,
      ...getReferenceProps(),
      type: "button",
      id: props.id
    },
    props.label,
    (choiceGroupValue == null ? void 0 : choiceGroupValue.length) ? /* @__PURE__ */ React.createElement(Badge, { text: choiceGroupValue.length, size: "small" }) : void 0,
    ((_s = props.inputType) == null ? void 0 : _s.slider) && showSliderValue && getSliderValues(props.inputType.slider),
    /* @__PURE__ */ React.createElement(Icon, { modifiers: "#arrow-down", size: "xs", className: "horizontal-filter-item__icon" })
  ), showClearButton && /* @__PURE__ */ React.createElement(
    Button,
    {
      id: "".concat(props.id, "-close"),
      onClick: onClearButtonClick,
      icon: "#close-round",
      link: true,
      text: labels.get("clear"),
      className: "horizontal-filter-item__clear-btn",
      "aria-labelledby": "".concat(props.id, "-close ").concat(props.id)
    }
  )), isMounted && /* @__PURE__ */ React.createElement(FloatingPortal, { root: (_t = props.portalContainer) != null ? _t : portalRoot }, /* @__PURE__ */ React.createElement(FloatingFocusManager, { context, modal: false, returnFocus: true }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: clsx("horizontal-filter-item__dropdown", "is-".concat(status), props.className),
      ...getFloatingProps({
        ref: refs.setFloating,
        style: {
          ...floatingStyles,
          zIndex: getZIndex()
        }
      })
    },
    /* @__PURE__ */ React.createElement(Card, { elevation: 5, background: "neutral-bg-level-3", className: "horizontal-filter-item__dropdown-inner" }, /* @__PURE__ */ React.createElement(
      CardContent,
      {
        padding: {
          bottom: "medium"
        }
      },
      ((_u = props.inputType) == null ? void 0 : _u.choiceGroup) && /* @__PURE__ */ React.createElement(ChoiceGroup, { ...props.inputType.choiceGroup }),
      ((_v = props.inputType) == null ? void 0 : _v.slider) && /* @__PURE__ */ React.createElement(
        Slider,
        {
          ...props.inputType.slider,
          onChange: (value) => {
            var _a2, _b2;
            setShowSliderValue(true);
            if ((_b2 = (_a2 = props.inputType) == null ? void 0 : _a2.slider) == null ? void 0 : _b2.onChange) {
              props.inputType.slider.onChange(value);
            }
          }
        }
      )
    ), /* @__PURE__ */ React.createElement(CardContent, { className: "horizontal-filter-item__dropdown-footer", background: "neutral-bg-level-4" }, /* @__PURE__ */ React.createElement(
      Button,
      {
        size: "small",
        ...props.saveButton,
        text: ((_w = props.saveButton) == null ? void 0 : _w.text) || labels.get("horizontalFilter.apply"),
        onClick: (e) => {
          var _a2, _b2;
          ((_a2 = props.saveButton) == null ? void 0 : _a2.onClick) && props.saveButton.onClick(e);
          handleModalOpenChange(false);
          ((_b2 = props.inputType) == null ? void 0 : _b2.slider) && setShowSliderValue(true);
        }
      }
    ), /* @__PURE__ */ React.createElement(
      Button,
      {
        link: true,
        ...props.resetButton,
        text: ((_x = props.resetButton) == null ? void 0 : _x.text) || labels.get("horizontalFilter.reset"),
        onClick: (e) => {
          var _a2;
          ((_a2 = props.resetButton) == null ? void 0 : _a2.onClick) && props.resetButton.onClick(e);
          handleModalOpenChange(false);
          setShowSliderValue(defaultIsActiveValue);
        }
      }
    )))
  ))));
};
const HorizontalFilterItem = (props) => {
  var _a, _b;
  const isSwitch = !(!!((_a = props.inputType) == null ? void 0 : _a.choiceGroup) || !!((_b = props.inputType) == null ? void 0 : _b.slider));
  if ("skeleton" in props) {
    const { skeleton } = props;
    return /* @__PURE__ */ React.createElement(Skeleton, { ...skeleton }, /* @__PURE__ */ React.createElement(SkeletonItem, { width: "128px", height: "48px" }));
  }
  if (isSwitch) {
    return /* @__PURE__ */ React.createElement(HorizontalFilterItemSwitch, { ...props });
  }
  return /* @__PURE__ */ React.createElement(HorizontalFilterItemChoiceGroupOrSlider, { ...props });
};
export {
  HorizontalFilterItem
};
